/**
 * API query parameter builder for DataList.
 *
 * Builds a flat `Record<string, string | number>` from the current list state
 * (pagination, sort, search, filters) merged with any default parameters
 * defined in the DataList configuration.  Empty / null values are omitted so
 * that the resulting object can be passed directly to a query-string serialiser.
 */

import type { DataListConfig, SortState } from '../types';
import {
  DEFAULT_SORT_PARAM,
  DEFAULT_DIRECTION_PARAM,
  DEFAULT_PAGE_PARAM,
  DEFAULT_PAGE_SIZE_PARAM,
  DEFAULT_SEARCH_PARAM,
} from '../constants';

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

/**
 * Current list state used to build query parameters.
 */
export interface ListParamState {
  page: number;
  pageSize: number;
  sort: SortState[];
  searchTerm: string;
  filterParams: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Returns `true` when a value should be excluded from the final params.
 * Excludes `null`, `undefined`, and empty strings.
 */
function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

// ---------------------------------------------------------------------------
// buildListParams
// ---------------------------------------------------------------------------

/**
 * Builds API query parameters from the current list state and config.
 *
 * Merge order (later entries win on key collision):
 *   1. `config.dataSource?.defaultParams`
 *   2. Pagination params (`page`, `per_page`)
 *   3. Sort params -- only when sort state is non-empty. Two shapes:
 *        - default (`directionFormat: 'separate'`): `sort_by` + `sort_dir`.
 *        - combined (`directionFormat: 'append-desc'`): a single `sort` param
 *          whose value is `<v>` (asc) or `<v>|desc` (desc). The pipe is left
 *          literal so axios's default serialiser encodes it to `%7C`.
 *      When `sorting.positionFromResponse` is set, `<v>` is the 1-based
 *      position of the column's mapped response field within `fieldOrder`
 *      rather than the column key; if the field cannot be located, no sort
 *      param is emitted.
 *   4. Search param (`search`) -- only when non-empty
 *   5. Filter params -- each key-value pair from `state.filterParams`
 *
 * After merging, any entry whose value is `null`, `undefined`, or `''` is
 * removed from the result.
 *
 * @param state      - The current list state (pagination, sort, search, filters).
 * @param config     - The full DataList configuration.
 * @param fieldOrder - The response row's field names in order (used only when
 *   `sorting.positionFromResponse` is set, to infer a column's numeric
 *   position). Optional and defaulted so existing callers compile unchanged.
 */
export function buildListParams(
  state: ListParamState,
  config: DataListConfig,
  fieldOrder: string[] = [],
): Record<string, string | number> {
  const params: Record<string, unknown> = {};

  // 1. Default params from config ----------------------------------------
  if (config.dataSource?.defaultParams) {
    for (const [key, value] of Object.entries(
      config.dataSource?.defaultParams,
    )) {
      params[key] = value;
    }
  }

  // 2. Pagination params --------------------------------------------------
  const pageParam = config.pagination?.pageParam ?? DEFAULT_PAGE_PARAM;
  const pageSizeParam =
    config.pagination?.pageSizeParam ?? DEFAULT_PAGE_SIZE_PARAM;

  params[pageParam] = state.page;
  params[pageSizeParam] = state.pageSize;

  // 3. Sort params (only first sort entry) --------------------------------
  if (state.sort.length > 0 && state.sort[0]) {
    const sortParam = config.sorting?.sortParam ?? DEFAULT_SORT_PARAM;
    const directionParam =
      config.sorting?.directionParam ?? DEFAULT_DIRECTION_PARAM;

    const { column, direction } = state.sort[0];

    // Resolve the value to send for `column`. By default this is the column
    // key itself; when `positionFromResponse` is set it is the column's
    // 1-based position within the response field order (inferred, never
    // hardcoded). A position that cannot be resolved (no data yet / unknown
    // field) yields `undefined` and skips the sort param entirely.
    let sortValue: string | number | undefined;
    if (config.sorting?.positionFromResponse) {
      const fieldName = config.dataSource.fieldMapping?.[column] ?? column;
      const idx = fieldOrder.indexOf(fieldName);
      sortValue = idx >= 0 ? idx + 1 : undefined;
    } else {
      sortValue = column;
    }

    // `sortValue !== undefined` (rather than `isEmptyValue`) narrows the type
    // to `string | number` and is the precise skip condition: only an
    // unresolved position yields `undefined`.
    if (sortValue !== undefined && sortValue !== '') {
      if (config.sorting?.directionFormat === 'append-desc') {
        // Single combined param: `<v>` asc, `<v>|desc` desc. The pipe is left
        // literal — axios's default param serialiser encodes it to `%7C`
        // (pre-encoding here would double-encode to `%257C`).
        const base = String(sortValue);
        params[sortParam] = direction === 'desc' ? `${base}|desc` : base;
      } else {
        params[sortParam] = sortValue;
        params[directionParam] = direction;
      }
    }
  }

  // 4. Search param (only when non-empty) ---------------------------------
  if (state.searchTerm !== '') {
    const searchParam =
      config.toolbar?.search?.paramName ?? DEFAULT_SEARCH_PARAM;

    params[searchParam] = state.searchTerm;
  }

  // 5. Filter params ------------------------------------------------------
  for (const [key, value] of Object.entries(state.filterParams)) {
    if (!isEmptyValue(value)) {
      params[key] = value;
    }
  }

  // 6. Clean up: remove empty values --------------------------------------
  const result: Record<string, string | number> = {};

  for (const [key, value] of Object.entries(params)) {
    if (!isEmptyValue(value)) {
      result[key] = value as string | number;
    }
  }

  return result;
}
